import React, { useCallback, useState } from 'react';
import { Upload, FileText, CheckCircle, AlertTriangle, RefreshCw, X, FileSpreadsheet, ArrowRight, Download, CheckSquare, Info } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DataTable } from '../../components/ui/DataTable';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Pagination } from '../../components/ui/Pagination';
import { showSuccess, showError, showInfo } from '../../components/ui/Toast';
import { PaymentRecord } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { mockBranches } from '../../data/mockData';
// Template column definitions
const TEMPLATE_COLUMNS = [{
  header: 'Receiver IBAN',
  key: 'receiverIban',
  maxLen: 24,
  required: true,
  type: 'AN',
  description: '24 digit valid IBAN'
}, {
  header: 'Receiver Name',
  key: 'receiverName',
  maxLen: 30,
  required: true,
  type: 'ANS',
  description: 'Beneficiary account title'
}, {
  header: 'Transaction Amount',
  key: 'amount',
  maxLen: null,
  required: true,
  type: 'Decimal',
  description: 'Numeric value (Decimal)'
}, {
  header: 'Payment Purpose',
  key: 'paymentPurpose',
  maxLen: 4,
  required: true,
  type: 'AN',
  description: '4 digit standard purpose code'
}, {
  header: 'Purpose Description',
  key: 'purposeDescription',
  maxLen: 40,
  required: true,
  type: 'AN',
  description: 'Payment purpose description'
}, {
  header: 'Narration',
  key: 'narration',
  maxLen: 120,
  required: true,
  type: 'AN',
  description: 'Any text up to 120 characters'
}, {
  header: 'Payment Reference',
  key: 'paymentReference',
  maxLen: 30,
  required: false,
  type: 'ANS',
  description: 'Reporting reference (optional)'
}, {
  header: 'Reserved 1',
  key: 'reserved1',
  maxLen: 120,
  required: false,
  type: 'ANS',
  description: 'Any text up to 120 chars (optional)'
}, {
  header: 'Reserved 2',
  key: 'reserved2',
  maxLen: 120,
  required: false,
  type: 'ANS',
  description: 'Any text up to 120 chars (optional)'
}];
interface PaymentUploadProps {
  onComplete: () => void;
  onViewBatch: (id: string) => void;
}
export function PaymentUpload({
  onComplete,
  onViewBatch
}: PaymentUploadProps) {
  const {
    currentUser
  } = useAuth();
  const [step, setStep] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showTemplateInfo, setShowTemplateInfo] = useState(false);
  const [records, setRecords] = useState<PaymentRecord[]>([]);
  const [recordPage, setRecordPage] = useState(1);
  const [balanceCheck, setBalanceCheck] = useState<{
    status: 'idle' | 'checking' | 'sufficient' | 'insufficient';
    balance: number;
  }>({
    status: 'idle',
    balance: 0
  });
  const recordsPerPage = 10;
  // Download XLSX template
  const handleDownloadTemplate = () => {
    const sampleData = [{
      'Receiver IBAN': 'PK36HABB0000000012345678',
      'Receiver Name': 'Muhammad Ali',
      'Transaction Amount': 50000.0,
      'Payment Purpose': '1011',
      'Purpose Description': 'Monthly Salary Payment',
      Narration: 'Jan 2024 Salary',
      'Payment Reference': 'SAL-2024-001',
      'Reserved 1': '',
      'Reserved 2': ''
    }, {
      'Receiver IBAN': 'PK36MUCB0000000087654321',
      'Receiver Name': 'Fatima Bibi',
      'Transaction Amount': 75000.5,
      'Payment Purpose': '1012',
      'Purpose Description': 'Vendor Supply Payment',
      Narration: 'Invoice INV-001',
      'Payment Reference': 'VND-2024-002',
      'Reserved 1': '',
      'Reserved 2': ''
    }];
    const ws = XLSX.utils.json_to_sheet(sampleData);
    // Set column widths
    ws['!cols'] = [{
      wch: 28
    }, {
      wch: 22
    }, {
      wch: 18
    }, {
      wch: 16
    }, {
      wch: 30
    }, {
      wch: 25
    }, {
      wch: 22
    }, {
      wch: 18
    }, {
      wch: 18
    } // Reserved 2
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payment Template');
    XLSX.writeFile(wb, 'CDNS_Payment_Template.xlsx');
    showSuccess('Template Downloaded', 'CDNS_Payment_Template.xlsx has been saved.');
  };
  // File validation
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    validateAndSetFile(file);
  }, []);
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) validateAndSetFile(file);
  };
  const validateAndSetFile = (file: File) => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      showError('Invalid file format', 'Please upload a .csv or .xlsx file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showError('File too large', 'File size must be less than 2 MB');
      return;
    }
    setUploadedFile(file);
  };
  // Validate a single record against the column spec
  const validateRecord = (row: any, index: number): string[] => {
    const errors: string[] = [];
    const iban = String(row['Receiver IBAN'] || row['receiverIban'] || '').trim();
    const name = String(row['Receiver Name'] || row['receiverName'] || '').trim();
    const amount = row['Transaction Amount'] ?? row['amount'];
    const purpose = String(row['Payment Purpose'] || row['paymentPurpose'] || '').trim();
    const purposeDesc = String(row['Purpose Description'] || row['purposeDescription'] || '').trim();
    const narration = String(row['Narration'] || row['narration'] || '').trim();
    const ref = String(row['Payment Reference'] || row['paymentReference'] || '').trim();
    const r1 = String(row['Reserved 1'] || row['reserved1'] || '').trim();
    const r2 = String(row['Reserved 2'] || row['reserved2'] || '').trim();
    if (!iban || iban.length !== 24) errors.push('Receiver IBAN must be exactly 24 characters');
    if (!iban.match(/^[A-Z]{2}[0-9A-Z]{22}$/i) && iban.length === 24) errors.push('Invalid IBAN format');
    if (!name) errors.push('Receiver Name is required');
    if (name.length > 30) errors.push('Receiver Name exceeds 30 characters');
    if (amount === undefined || amount === null || amount === '' || isNaN(Number(amount)) || Number(amount) <= 0) errors.push('Transaction Amount must be a positive number');
    if (!purpose) errors.push('Payment Purpose is required');
    if (purpose.length > 4) errors.push('Payment Purpose exceeds 4 characters');
    if (!purposeDesc) errors.push('Purpose Description is required');
    if (purposeDesc.length > 40) errors.push('Purpose Description exceeds 40 characters');
    if (!narration) errors.push('Narration is required');
    if (narration.length > 120) errors.push('Narration exceeds 120 characters');
    if (ref.length > 30) errors.push('Payment Reference exceeds 30 characters');
    if (r1.length > 120) errors.push('Reserved 1 exceeds 120 characters');
    if (r2.length > 120) errors.push('Reserved 2 exceeds 120 characters');
    return errors;
  };
  const handleUpload = () => {
    if (!uploadedFile) return;
    setIsUploading(true);
    // Simulate parsing — in production, use XLSX.read() to parse the actual file
    setTimeout(() => {
      const mockParsedRecords: PaymentRecord[] = [{
        id: 1,
        batchId: 'temp-1',
        receiverIban: 'PK36HABB0000000012345678',
        receiverName: 'Muhammad Ali',
        amount: 50000,
        paymentPurpose: '1011',
        purposeDescription: 'Monthly Salary',
        narration: 'Jan 2024 Salary',
        paymentReference: 'SAL-2024-001',
        reserved1: '',
        reserved2: '',
        bankBic: 'HABB',
        titleFetchStatus: 'Pending',
        status: 'Pending',
        validationErrors: []
      }, {
        id: 2,
        batchId: 'temp-1',
        receiverIban: 'PK36MUCB0000000087654321',
        receiverName: 'Fatima Bibi',
        amount: 75000.5,
        paymentPurpose: '1012',
        purposeDescription: 'Supply Payment',
        narration: 'Invoice INV-001',
        paymentReference: 'VND-2024-002',
        reserved1: '',
        reserved2: '',
        bankBic: 'MUCB',
        titleFetchStatus: 'Pending',
        status: 'Pending',
        validationErrors: []
      }, {
        id: 3,
        batchId: 'temp-1',
        receiverIban: 'PK36BAHL0000000011223344',
        receiverName: 'Usman Khan',
        amount: 25000,
        paymentPurpose: '1013',
        purposeDescription: 'Monthly Pension',
        narration: 'Pension Jan 2024',
        paymentReference: '',
        reserved1: '',
        reserved2: '',
        bankBic: 'BAHL',
        titleFetchStatus: 'Pending',
        status: 'Pending',
        validationErrors: []
      }, {
        id: 4,
        batchId: 'temp-1',
        receiverIban: 'PK36INVALID00000000',
        receiverName: 'A Very Long Name That Exceeds The Thirty Character Limit',
        amount: -500,
        paymentPurpose: 'TOOLONG',
        purposeDescription: 'Misc',
        narration: 'Test',
        paymentReference: '',
        reserved1: '',
        reserved2: '',
        bankBic: 'UNKNOWN',
        titleFetchStatus: 'Pending',
        status: 'Pending',
        validationErrors: []
      }];
      // Run validation on each record
      const validated = mockParsedRecords.map((r, i) => {
        const errors = validateRecord({
          'Receiver IBAN': r.receiverIban,
          'Receiver Name': r.receiverName,
          'Transaction Amount': r.amount,
          'Payment Purpose': r.paymentPurpose,
          'Purpose Description': r.purposeDescription,
          Narration: r.narration,
          'Payment Reference': r.paymentReference,
          'Reserved 1': r.reserved1,
          'Reserved 2': r.reserved2
        }, i);
        return {
          ...r,
          validationErrors: errors.length > 0 ? errors : undefined,
          status: errors.length > 0 ? 'Failed' as const : 'Pending' as const
        };
      });
      if (validated.length > 1000) {
        setIsUploading(false);
        showError('Too many records', 'File cannot contain more than 1,000 records');
        return;
      }
      setRecords(validated);
      setIsUploading(false);
      setRecordPage(1);
      setStep(2);
      const validCount = validated.filter(r => !r.validationErrors?.length).length;
      showSuccess('File parsed successfully', `${validated.length} records found — ${validCount} valid, ${validated.length - validCount} with errors`);
    }, 1500);
  };
  // Title Fetch
  const handleTitleFetch = (retryFailed = false) => {
    setIsFetching(true);
    setTimeout(() => {
      setRecords(prev => prev.map(r => {
        if (retryFailed && r.titleFetchStatus !== 'Failed') return r;
        if (r.status === 'Failed' && r.validationErrors?.length) return r;
        const isSuccess = Math.random() > 0.2;
        return {
          ...r,
          titleFetchStatus: isSuccess ? 'Success' : 'Failed',
          titleFetchResult: isSuccess ? r.receiverName.toUpperCase() : undefined,
          status: isSuccess ? 'TitleFetchCompleted' : 'Pending'
        };
      }));
      setIsFetching(false);
      setRecordPage(1);
      setStep(3);
    }, 2000);
  };
  const handleSubmit = (onlySuccessful = false) => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSubmitConfirm(false);
      showSuccess('Batch submitted for approval', 'Batch ID: #BATCH-NEW-001');
      onViewBatch('batch-new-001');
    }, 1500);
  };
  // Branch balance check
  const handleBalanceCheck = () => {
    setBalanceCheck({
      status: 'checking',
      balance: 0
    });
    // Simulate API call to check branch account balance
    setTimeout(() => {
      // Mock balance: generate a realistic balance for the branch
      const branch = mockBranches.find(b => b.id === currentUser?.branchId);
      const mockBalance = 5000000 + Math.floor(Math.random() * 10000000); // PKR 5M-15M
      const isSufficient = mockBalance >= totalAmount;
      setBalanceCheck({
        status: isSufficient ? 'sufficient' : 'insufficient',
        balance: mockBalance
      });
      if (isSufficient) {
        showSuccess('Balance Verified', `Branch account has sufficient funds: PKR ${mockBalance.toLocaleString()}`);
      } else {
        showError('Insufficient Balance', `Branch balance PKR ${mockBalance.toLocaleString()} is less than batch total PKR ${totalAmount.toLocaleString()}`);
      }
    }, 1500);
  };
  // Calculations
  const totalAmount = records.reduce((sum, r) => sum + r.amount, 0);
  const validRecords = records.filter(r => !r.validationErrors?.length).length;
  const invalidRecords = records.length - validRecords;
  const successfulTitleFetches = records.filter(r => r.titleFetchStatus === 'Success').length;
  const failedTitleFetches = records.filter(r => r.titleFetchStatus === 'Failed' && !r.validationErrors?.length).length;
  // Pagination helper
  const paginate = <T,>(data: T[]) => {
    const totalPages = Math.ceil(data.length / recordsPerPage);
    const sliced = data.slice((recordPage - 1) * recordsPerPage, recordPage * recordsPerPage);
    return {
      sliced,
      totalPages
    };
  };
  return <div className="space-y-8 max-w-6xl mx-auto" data-id="element-385">
      {/* Stepper */}
      <div className="flex items-center justify-center space-x-4 mb-8" data-id="element-386">
        {[{
        num: 1,
        label: 'Upload'
      }, {
        num: 2,
        label: 'Validate'
      }, {
        num: 3,
        label: 'Title Fetch'
      }, {
        num: 4,
        label: 'Submit'
      }].map((s, idx) => <div key={s.num} className="flex items-center" data-id="element-387">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors duration-300 ${step >= s.num ? 'bg-[#1B6B3A] text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-388">
              {step > s.num ? <CheckCircle className="w-4 h-4" data-id="element-389" /> : s.num}
            </div>
            <span className={`ml-2 text-sm font-medium ${step >= s.num ? 'text-[#1B6B3A]' : 'text-gray-500'}`} data-id="element-390">
              {s.label}
            </span>
            {idx < 3 && <div className={`w-12 h-0.5 mx-4 ${step > s.num ? 'bg-[#1B6B3A]' : 'bg-gray-200'}`} data-id="element-391"></div>}
          </div>)}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && <div className="space-y-6" data-id="element-392">
          <Card className="max-w-3xl mx-auto py-10 px-8" data-id="element-393">
            <div className="flex items-center justify-between mb-6" data-id="element-394">
              <div data-id="element-395">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-396">
                  Upload Payment File
                </h3>
                <p className="text-sm text-gray-500 mt-0.5" data-id="element-397">
                  Download the template, fill in payment data, and upload
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate} leftIcon={<Download className="w-4 h-4" data-id="element-399" />} data-id="element-398">
                Download Template (.xlsx)
              </Button>
            </div>

            {/* Column spec info */}
            <div className="mb-6" data-id="element-400">
              <button type="button" onClick={() => setShowTemplateInfo(!showTemplateInfo)} className="flex items-center gap-1.5 text-sm font-medium text-[#1B6B3A] hover:underline" data-id="element-401">
                <Info className="w-4 h-4" data-id="element-402" />
                {showTemplateInfo ? 'Hide' : 'View'} template column
                specifications
              </button>
              {showTemplateInfo && <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden" data-id="element-403">
                  <table className="w-full text-sm" data-id="element-404">
                    <thead className="bg-gray-50" data-id="element-405">
                      <tr data-id="element-406">
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase" data-id="element-407">
                          Column
                        </th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase" data-id="element-408">
                          Type
                        </th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase" data-id="element-409">
                          Max Length
                        </th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase" data-id="element-410">
                          Required
                        </th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase" data-id="element-411">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100" data-id="element-412">
                      {TEMPLATE_COLUMNS.map(col => <tr key={col.key} data-id="element-413">
                          <td className="px-4 py-2 font-medium text-gray-900" data-id="element-414">
                            {col.header}
                          </td>
                          <td className="px-4 py-2 text-gray-600 font-mono text-xs" data-id="element-415">
                            {col.type}
                          </td>
                          <td className="px-4 py-2 text-gray-600" data-id="element-416">
                            {col.maxLen ?? '—'}
                          </td>
                          <td className="px-4 py-2" data-id="element-417">
                            {col.required ? <span className="text-xs font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded" data-id="element-418">
                                Required
                              </span> : <span className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-419">
                                Optional
                              </span>}
                          </td>
                          <td className="px-4 py-2 text-gray-500" data-id="element-420">
                            {col.description}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}
            </div>

            {/* Drop zone */}
            <div className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors duration-200 ${uploadedFile ? 'border-[#1B6B3A] bg-green-50' : 'border-gray-300 hover:border-[#1B6B3A] hover:bg-gray-50'}`} onDragOver={e => e.preventDefault()} onDrop={handleDrop} data-id="element-421">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100" data-id="element-422">
                {uploadedFile ? <FileSpreadsheet className="w-8 h-8 text-[#1B6B3A]" data-id="element-423" /> : <Upload className="w-8 h-8 text-[#1B6B3A]" data-id="element-424" />}
              </div>

              {uploadedFile ? <div className="space-y-2" data-id="element-425">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-426">
                    {uploadedFile.name}
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-427">
                    {(uploadedFile.size / 1024).toFixed(1)} KB
                  </p>
                  <Button variant="ghost" size="sm" onClick={() => setUploadedFile(null)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-id="element-428">
                    Remove File
                  </Button>
                </div> : <div className="space-y-3" data-id="element-429">
                  <p className="text-gray-600" data-id="element-430">
                    Drag and drop your payment file here, or
                  </p>
                  <div data-id="element-431">
                    <input type="file" className="hidden" id="file-upload" accept=".csv,.xlsx" onChange={handleFileSelect} data-id="element-432" />
                    <Button variant="outline" onClick={() => document.getElementById('file-upload')?.click()} data-id="element-433">
                      Browse Files
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400" data-id="element-434">
                    Supported: .xlsx, .csv &nbsp;•&nbsp; Max 1,000 records
                    &nbsp;•&nbsp; Max 2 MB
                  </p>
                </div>}
            </div>

            <div className="mt-6 flex justify-end" data-id="element-435">
              <Button onClick={handleUpload} disabled={!uploadedFile} isLoading={isUploading} size="lg" rightIcon={<ArrowRight className="w-4 h-4" data-id="element-437" />} data-id="element-436">
                Upload & Validate
              </Button>
            </div>
          </Card>
        </div>}

      {/* Step 2: Validation Summary */}
      {step === 2 && (() => {
      const {
        sliced,
        totalPages
      } = paginate(records);
      return <div className="space-y-6" data-id="element-438">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-439">
                <Card className="text-center p-4" data-id="element-440">
                  <p className="text-sm text-gray-500" data-id="element-441">Total Records</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-442">
                    {records.length}
                  </p>
                </Card>
                <Card className="text-center p-4" data-id="element-443">
                  <p className="text-sm text-gray-500" data-id="element-444">Valid Records</p>
                  <p className="text-2xl font-bold text-green-600" data-id="element-445">
                    {validRecords}
                  </p>
                </Card>
                <Card className="text-center p-4" data-id="element-446">
                  <p className="text-sm text-gray-500" data-id="element-447">Invalid Records</p>
                  <p className="text-2xl font-bold text-red-600" data-id="element-448">
                    {invalidRecords}
                  </p>
                </Card>
                <Card className="text-center p-4" data-id="element-449">
                  <p className="text-sm text-gray-500" data-id="element-450">Total Amount</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-451">
                    PKR {totalAmount.toLocaleString()}
                  </p>
                </Card>
              </div>

              <Card title="Parsed Records & Validation" data-id="element-452">
                <div className="overflow-x-auto" data-id="element-453">
                  <DataTable columns={[{
              header: '#',
              accessor: r => <span className="text-gray-400 text-xs" data-id="element-455">{r.id}</span>
            }, {
              header: 'Receiver IBAN',
              accessor: r => <div data-id="element-456">
                            <span className="font-mono text-xs" data-id="element-457">
                              {r.receiverIban}
                            </span>
                            {r.validationErrors?.filter(e => e.toLowerCase().includes('iban')).map((e, i) => <p key={i} className="text-xs text-red-600 mt-0.5" data-id="element-458">
                                  {e}
                                </p>)}
                          </div>
            }, {
              header: 'Receiver Name',
              accessor: r => <div data-id="element-459">
                            <span className="text-sm" data-id="element-460">{r.receiverName}</span>
                            {r.validationErrors?.filter(e => e.toLowerCase().includes('name')).map((e, i) => <p key={i} className="text-xs text-red-600 mt-0.5" data-id="element-461">
                                  {e}
                                </p>)}
                          </div>
            }, {
              header: 'Amount',
              accessor: r => <div data-id="element-462">
                            <span className="font-mono" data-id="element-463">
                              {r.amount.toLocaleString()}
                            </span>
                            {r.validationErrors?.filter(e => e.toLowerCase().includes('amount')).map((e, i) => <p key={i} className="text-xs text-red-600 mt-0.5" data-id="element-464">
                                  {e}
                                </p>)}
                          </div>
            }, {
              header: 'Purpose',
              accessor: r => <div data-id="element-465">
                            <span className="font-mono text-xs" data-id="element-466">
                              {r.paymentPurpose}
                            </span>
                            {r.validationErrors?.filter(e => e.toLowerCase().includes('purpose') && !e.toLowerCase().includes('description')).map((e, i) => <p key={i} className="text-xs text-red-600 mt-0.5" data-id="element-467">
                                  {e}
                                </p>)}
                          </div>
            }, {
              header: 'Description',
              accessor: r => <div className="max-w-[180px] truncate" title={r.purposeDescription} data-id="element-468">
                            <span className="text-sm" data-id="element-469">
                              {r.purposeDescription}
                            </span>
                          </div>
            }, {
              header: 'Narration',
              accessor: r => <div className="max-w-[150px] truncate" title={r.narration} data-id="element-470">
                            <span className="text-sm" data-id="element-471">{r.narration}</span>
                          </div>
            }, {
              header: 'Ref',
              accessor: r => <span className="text-xs text-gray-500" data-id="element-472">
                            {r.paymentReference || '—'}
                          </span>
            }, {
              header: 'Status',
              accessor: r => <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${r.validationErrors?.length ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} data-id="element-473">
                            {r.validationErrors?.length ? `${r.validationErrors.length} Error${r.validationErrors.length > 1 ? 's' : ''}` : 'Valid'}
                          </span>
            }]} data={sliced} data-id="element-454" />
                </div>
                <Pagination currentPage={recordPage} totalPages={totalPages} onPageChange={setRecordPage} data-id="element-474" />
              </Card>

              <div className="flex justify-between" data-id="element-475">
                <Button variant="outline" onClick={() => {
            setStep(1);
            setRecordPage(1);
          }} data-id="element-476">
                  Back to Upload
                </Button>
                <Button onClick={() => handleTitleFetch(false)} rightIcon={<RefreshCw className="w-4 h-4" data-id="element-478" />} disabled={validRecords === 0} data-id="element-477">
                  Initiate Title Fetch ({validRecords} valid)
                </Button>
              </div>
            </div>;
    })()}

      {/* Step 3: Title Fetch Results */}
      {step === 3 && (() => {
      const fetchableRecords = records.filter(r => !r.validationErrors?.length);
      const {
        sliced,
        totalPages
      } = paginate(fetchableRecords);
      return <div className="space-y-6" data-id="element-479">
              {/* Summary bar */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-480">
                <Card className="text-center p-4" data-id="element-481">
                  <p className="text-sm text-gray-500" data-id="element-482">Total Fetched</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-483">
                    {fetchableRecords.length}
                  </p>
                </Card>
                <Card className="text-center p-4" data-id="element-484">
                  <p className="text-sm text-gray-500" data-id="element-485">Successful</p>
                  <p className="text-2xl font-bold text-green-600" data-id="element-486">
                    {successfulTitleFetches}
                  </p>
                </Card>
                <Card className="text-center p-4" data-id="element-487">
                  <p className="text-sm text-gray-500" data-id="element-488">Failed</p>
                  <p className="text-2xl font-bold text-red-600" data-id="element-489">
                    {failedTitleFetches}
                  </p>
                </Card>
                <Card className="text-center p-4" data-id="element-490">
                  <p className="text-sm text-gray-500" data-id="element-491">Successful Amount</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-492">
                    PKR{' '}
                    {records.filter(r => r.titleFetchStatus === 'Success').reduce((s, r) => s + r.amount, 0).toLocaleString()}
                  </p>
                </Card>
              </div>

              <div className="flex items-center justify-between" data-id="element-493">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-494">
                  Title Fetch Results
                </h3>
                <Button variant="outline" onClick={() => {
            handleTitleFetch(true);
            setRecordPage(1);
          }} isLoading={isFetching} disabled={failedTitleFetches === 0} leftIcon={<RefreshCw className="w-4 h-4" data-id="element-496" />} data-id="element-495">
                  Retry Failed ({failedTitleFetches})
                </Button>
              </div>

              <Card data-id="element-497">
                <div className="overflow-x-auto" data-id="element-498">
                  <DataTable columns={[{
              header: '#',
              accessor: r => <span className="text-gray-400 text-xs" data-id="element-500">{r.id}</span>
            }, {
              header: 'Receiver IBAN',
              accessor: 'receiverIban',
              className: 'font-mono text-xs'
            }, {
              header: 'Expected Name',
              accessor: 'receiverName'
            }, {
              header: 'Fetched Title',
              accessor: r => r.titleFetchResult ? <span className="font-semibold text-green-700" data-id="element-501">
                              {r.titleFetchResult}
                            </span> : <span className="text-gray-400 italic" data-id="element-502">
                              Not fetched
                            </span>
            }, {
              header: 'Amount',
              accessor: r => <span className="font-mono" data-id="element-503">
                            PKR {r.amount.toLocaleString()}
                          </span>
            }, {
              header: 'Purpose',
              accessor: r => <span className="font-mono text-xs" data-id="element-504">
                            {r.paymentPurpose}
                          </span>
            }, {
              header: 'Narration',
              accessor: r => <div className="max-w-[150px] truncate" title={r.narration} data-id="element-505">
                            {r.narration}
                          </div>
            }, {
              header: 'Fetch Status',
              accessor: r => <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${r.titleFetchStatus === 'Success' ? 'bg-green-100 text-green-800' : r.titleFetchStatus === 'Failed' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-506">
                            {r.titleFetchStatus === 'Success' ? <CheckCircle className="w-3 h-3 mr-1" data-id="element-507" /> : r.titleFetchStatus === 'Failed' ? <AlertTriangle className="w-3 h-3 mr-1" data-id="element-508" /> : null}
                            {r.titleFetchStatus}
                          </span>
            }]} data={sliced} data-id="element-499" />
                </div>
                <Pagination currentPage={recordPage} totalPages={totalPages} onPageChange={setRecordPage} data-id="element-509" />
              </Card>

              <div className="flex justify-between" data-id="element-510">
                <Button variant="outline" onClick={() => {
            setStep(2);
            setRecordPage(1);
          }} data-id="element-511">
                  Back
                </Button>
                <div className="space-x-3" data-id="element-512">
                  <Button variant="secondary" onClick={() => handleSubmit(true)} disabled={successfulTitleFetches === 0} data-id="element-513">
                    Submit Successful Only ({successfulTitleFetches})
                  </Button>
                  <Button onClick={() => setStep(4)} disabled={records.some(r => r.titleFetchStatus === 'Pending' && !r.validationErrors?.length)} data-id="element-514">
                    Proceed to Review All
                  </Button>
                </div>
              </div>
            </div>;
    })()}

      {/* Step 4: Final Review */}
      {step === 4 && <Card className="max-w-2xl mx-auto text-center py-12" data-id="element-515">
          <div className="mb-8" data-id="element-516">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-517">
              <FileText className="w-10 h-10 text-[#1B6B3A]" data-id="element-518" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900" data-id="element-519">
              Ready to Submit
            </h3>
            <p className="text-gray-500 mt-2 max-w-md mx-auto" data-id="element-520">
              You are about to submit{' '}
              <span className="font-bold text-gray-900" data-id="element-521">
                {validRecords} records
              </span>{' '}
              totaling{' '}
              <span className="font-bold text-gray-900" data-id="element-522">
                PKR {totalAmount.toLocaleString()}
              </span>
              .
            </p>

            {/* Balance Verification Section */}
            <div className="max-w-md mx-auto mt-6" data-id="element-523">
              {balanceCheck.status === 'idle' && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left" data-id="element-524">
                  <div className="flex items-start" data-id="element-525">
                    <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-526" />
                    <div className="ml-3" data-id="element-527">
                      <h4 className="text-sm font-medium text-blue-800" data-id="element-528">
                        Balance Verification Required
                      </h4>
                      <p className="mt-1 text-sm text-blue-700" data-id="element-529">
                        Branch account balance must be verified before
                        submission.
                      </p>
                      <Button size="sm" className="mt-3" onClick={handleBalanceCheck} data-id="element-530">
                        Verify Branch Balance
                      </Button>
                    </div>
                  </div>
                </div>}

              {balanceCheck.status === 'checking' && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left" data-id="element-531">
                  <div className="flex items-center" data-id="element-532">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 flex-shrink-0" data-id="element-533"></div>
                    <p className="ml-3 text-sm font-medium text-blue-800" data-id="element-534">
                      Checking branch account balance...
                    </p>
                  </div>
                </div>}

              {balanceCheck.status === 'sufficient' && <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left" data-id="element-535">
                  <div className="flex items-start" data-id="element-536">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-537" />
                    <div className="ml-3" data-id="element-538">
                      <h4 className="text-sm font-medium text-green-800" data-id="element-539">
                        Balance Verified ✓
                      </h4>
                      <div className="mt-2 text-sm text-green-700 space-y-1" data-id="element-540">
                        <p data-id="element-541">
                          Branch Balance:{' '}
                          <span className="font-bold" data-id="element-542">
                            PKR {balanceCheck.balance.toLocaleString()}
                          </span>
                        </p>
                        <p data-id="element-543">
                          Batch Total:{' '}
                          <span className="font-bold" data-id="element-544">
                            PKR {totalAmount.toLocaleString()}
                          </span>
                        </p>
                        <p data-id="element-545">
                          Remaining After:{' '}
                          <span className="font-bold" data-id="element-546">
                            PKR{' '}
                            {(balanceCheck.balance - totalAmount).toLocaleString()}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>}

              {balanceCheck.status === 'insufficient' && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left" data-id="element-547">
                  <div className="flex items-start" data-id="element-548">
                    <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-549" />
                    <div className="ml-3" data-id="element-550">
                      <h4 className="text-sm font-medium text-red-800" data-id="element-551">
                        Insufficient Balance
                      </h4>
                      <div className="mt-2 text-sm text-red-700 space-y-1" data-id="element-552">
                        <p data-id="element-553">
                          Branch Balance:{' '}
                          <span className="font-bold" data-id="element-554">
                            PKR {balanceCheck.balance.toLocaleString()}
                          </span>
                        </p>
                        <p data-id="element-555">
                          Batch Total:{' '}
                          <span className="font-bold" data-id="element-556">
                            PKR {totalAmount.toLocaleString()}
                          </span>
                        </p>
                        <p data-id="element-557">
                          Shortfall:{' '}
                          <span className="font-bold" data-id="element-558">
                            PKR{' '}
                            {(totalAmount - balanceCheck.balance).toLocaleString()}
                          </span>
                        </p>
                      </div>
                      <Button size="sm" variant="outline" className="mt-3" onClick={handleBalanceCheck} data-id="element-559">
                        Re-check Balance
                      </Button>
                    </div>
                  </div>
                </div>}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6 text-left max-w-md mx-auto" data-id="element-560">
              <div className="flex" data-id="element-561">
                <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0" data-id="element-562" />
                <div className="ml-3" data-id="element-563">
                  <h3 className="text-sm font-medium text-amber-800" data-id="element-564">
                    Important Note
                  </h3>
                  <p className="mt-1 text-sm text-amber-700" data-id="element-565">
                    This batch will be locked and sent to the Branch Checker for
                    final approval. You cannot edit records after submission.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-center space-x-4" data-id="element-566">
            <Button variant="outline" onClick={() => {
          setStep(3);
          setBalanceCheck({
            status: 'idle',
            balance: 0
          });
        }} data-id="element-567">
              Back
            </Button>
            <Button onClick={() => setShowSubmitConfirm(true)} size="lg" disabled={balanceCheck.status !== 'sufficient'} data-id="element-568">
              Submit for Approval
            </Button>
          </div>
        </Card>}

      <ConfirmDialog open={showSubmitConfirm} title="Confirm Submission" message={`Are you sure you want to submit this batch of ${validRecords} records? This action cannot be undone.`} onConfirm={() => handleSubmit(false)} onCancel={() => setShowSubmitConfirm(false)} confirmLabel="Yes, Submit Batch" variant="success" data-id="element-569" />
    </div>;
}